import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { theme } from '../../../theme';

interface Tag {
  id: string;
  label: string;
  emoji: string;
  category: string;
}

interface CreateVenueReviewModalProps {
  venueName: string;
  venueId: string;
  onClose: () => void;
  onSubmit: (reviewData: {
    rating: number;
    tags: string[];
    customTags: string[];
    comment?: string;
  }) => Promise<void>;
}

const POSITIVE_TAGS: Tag[] = [
  { id: 'well_lit', label: 'Bien iluminada', emoji: '💡', category: 'lighting' },
  { id: 'perfect_grass', label: 'Césped impecable', emoji: '⚽', category: 'surface' },
  { id: 'clean_bathrooms', label: 'Baños limpios', emoji: '🚿', category: 'facilities' },
  { id: 'great_buffet', label: 'Buffet rico', emoji: '🍔', category: 'amenities' },
  { id: 'friendly_owner', label: 'Dueño amable', emoji: '😊', category: 'service' },
  { id: 'good_parking', label: 'Buen estacionamiento', emoji: '🅿️', category: 'amenities' },
  { id: 'fast_response', label: 'Responde rápido', emoji: '⚡', category: 'service' },
  { id: 'fair_price', label: 'Precio justo', emoji: '💰', category: 'value' },
  { id: 'always_available', label: 'Siempre disponible', emoji: '📅', category: 'availability' },
  { id: 'secure_area', label: 'Zona segura', emoji: '🔒', category: 'location' },
];

const NEGATIVE_TAGS: Tag[] = [
  { id: 'dirty_bathrooms', label: 'Baños sucios', emoji: '🚽', category: 'facilities' },
  { id: 'slippery_grass', label: 'Césped resbaladizo', emoji: '🧊', category: 'surface' },
  { id: 'bad_lighting', label: 'Mala iluminación', emoji: '🌑', category: 'lighting' },
  { id: 'rude_owner', label: 'Dueño mala onda', emoji: '😠', category: 'service' },
  { id: 'expensive', label: 'Muy caro', emoji: '💸', category: 'value' },
  { id: 'hard_to_book', label: 'Difícil de reservar', emoji: '❌', category: 'availability' },
  { id: 'no_parking', label: 'Sin estacionamiento', emoji: '🚫', category: 'amenities' },
  { id: 'cancels_often', label: 'Cancela seguido', emoji: '📵', category: 'service' },
  { id: 'unsafe_area', label: 'Zona insegura', emoji: '⚠️', category: 'location' },
  { id: 'poor_facilities', label: 'Instalaciones malas', emoji: '🏚️', category: 'facilities' },
];

const CreateVenueReviewModal: React.FC<CreateVenueReviewModalProps> = ({
  venueName,
  venueId,
  onClose,
  onSubmit,
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'positive' | 'negative' | 'custom'>('positive');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentTags = activeTab === 'positive' ? POSITIVE_TAGS : activeTab === 'negative' ? NEGATIVE_TAGS : [];
  const maxPredefinedTags = 5;
  const maxCustomTags = 3;

  const handleTagClick = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((t) => t !== tagId));
    } else if (selectedTags.length < maxPredefinedTags) {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleAddCustomTag = () => {
    const trimmed = customTagInput.trim();
    if (trimmed && customTags.length < maxCustomTags && !customTags.includes(trimmed)) {
      setCustomTags([...customTags, trimmed]);
      setCustomTagInput('');
    }
  };

  const handleRemoveCustomTag = (tag: string) => {
    setCustomTags(customTags.filter((t) => t !== tag));
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Por favor selecciona una calificación');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        rating,
        tags: selectedTags,
        customTags,
        comment: comment.trim() || undefined,
      });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting review:', error);
      alert('Error al enviar la review. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(24, 26, 27, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '24px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const titleSectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '28px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '0.5px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.textSecondary,
    margin: 0,
  };

  const closeBtnStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: '600',
    color: theme.colors.text,
  };

  const starsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const tabsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    backgroundColor: theme.colors.background,
    borderRadius: '12px',
    padding: '4px',
    height: '44px',
  };

  const getTabStyle = (tab: 'positive' | 'negative' | 'custom'): React.CSSProperties => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: '8px',
    backgroundColor: activeTab === tab ? theme.colors.primary : 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    padding: '12px 16px',
  });

  const tabIconStyle = (active: boolean): React.CSSProperties => ({
    fontSize: '16px',
    opacity: active ? 1 : 0.6,
  });

  const tabTextStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    fontWeight: active ? '600' : '400',
    color: active ? '#000' : theme.colors.textSecondary,
  });

  const tagsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  };

  const getTagStyle = (isSelected: boolean, isDisabled: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderRadius: '12px',
    backgroundColor: theme.colors.background,
    border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled && !isSelected ? 0.5 : 1,
    transition: 'all 0.2s',
  });

  const tagEmojiStyle: React.CSSProperties = {
    fontSize: '18px',
  };

  const tagLabelStyle = (isSelected: boolean): React.CSSProperties => ({
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    fontWeight: isSelected ? '600' : '400',
    color: isSelected ? theme.colors.text : theme.colors.textSecondary,
    flex: 1,
  });

  const customTagsContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const customTagInputStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '12px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.text,
    outline: 'none',
  };

  const addBtnStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: '12px',
    backgroundColor: theme.colors.primary,
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    fontWeight: '600',
    color: '#000',
    cursor: customTags.length >= maxCustomTags || !customTagInput.trim() ? 'not-allowed' : 'pointer',
    opacity: customTags.length >= maxCustomTags || !customTagInput.trim() ? 0.5 : 1,
  };

  const customTagsListStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  };

  const customTagBadgeStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '20px',
    backgroundColor: theme.colors.primary,
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: '600',
    color: '#000',
  };

  const removeBtnStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.2)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  };

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    height: '100px',
    padding: '12px 16px',
    borderRadius: '12px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.text,
    resize: 'none',
    outline: 'none',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const counterStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.textSecondary,
  };

  const submitBtnStyle: React.CSSProperties = {
    padding: '16px 32px',
    borderRadius: '12px',
    backgroundColor: rating === 0 ? theme.colors.border : theme.colors.primary,
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: 'bold',
    color: rating === 0 ? theme.colors.textSecondary : '#000',
    cursor: rating === 0 || isSubmitting ? 'not-allowed' : 'pointer',
    opacity: rating === 0 || isSubmitting ? 0.5 : 1,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={titleSectionStyle}>
            <h2 style={titleStyle}>Dejá tu review</h2>
            <p style={subtitleStyle}>{venueName}</p>
          </div>
          <button style={closeBtnStyle} onClick={onClose}>
            <X size={20} color={theme.colors.textSecondary} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Rating */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Calificación</label>
            <div style={starsContainerStyle}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <Star
                    size={32}
                    fill={star <= (hoverRating || rating) ? '#FFD600' : 'transparent'}
                    color={star <= (hoverRating || rating) ? '#FFD600' : theme.colors.textSecondary}
                    style={{ opacity: star <= (hoverRating || rating) ? 1 : 0.3 }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Tags (máx {maxPredefinedTags} predefinidos)</label>
            <div style={tabsContainerStyle}>
              <button style={getTabStyle('positive')} onClick={() => setActiveTab('positive')}>
                <span style={tabIconStyle(activeTab === 'positive')}>😊</span>
                <span style={tabTextStyle(activeTab === 'positive')}>Positivos</span>
              </button>
              <button style={getTabStyle('negative')} onClick={() => setActiveTab('negative')}>
                <span style={tabIconStyle(activeTab === 'negative')}>😠</span>
                <span style={tabTextStyle(activeTab === 'negative')}>Negativos</span>
              </button>
              <button style={getTabStyle('custom')} onClick={() => setActiveTab('custom')}>
                <span style={tabIconStyle(activeTab === 'custom')}>✏️</span>
                <span style={tabTextStyle(activeTab === 'custom')}>Custom</span>
              </button>
            </div>

            {/* Predefined Tags */}
            {activeTab !== 'custom' && (
              <div style={tagsGridStyle}>
                {currentTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  const isDisabled = !isSelected && selectedTags.length >= maxPredefinedTags;
                  return (
                    <div
                      key={tag.id}
                      style={getTagStyle(isSelected, isDisabled)}
                      onClick={() => !isDisabled && handleTagClick(tag.id)}
                    >
                      <span style={tagEmojiStyle}>{tag.emoji}</span>
                      <span style={tagLabelStyle(isSelected)}>{tag.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Custom Tags */}
            {activeTab === 'custom' && (
              <div style={customTagsContainerStyle}>
                <div style={customTagInputStyle}>
                  <input
                    type="text"
                    placeholder="Escribe tu tag personalizado..."
                    value={customTagInput}
                    onChange={(e) => setCustomTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
                    maxLength={30}
                    style={inputStyle}
                  />
                  <button
                    style={addBtnStyle}
                    onClick={handleAddCustomTag}
                    disabled={customTags.length >= maxCustomTags || !customTagInput.trim()}
                  >
                    Agregar
                  </button>
                </div>
                {customTags.length > 0 && (
                  <div style={customTagsListStyle}>
                    {customTags.map((tag) => (
                      <div key={tag} style={customTagBadgeStyle}>
                        {tag}
                        <button style={removeBtnStyle} onClick={() => handleRemoveCustomTag(tag)}>
                          <X size={10} color="#000" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ ...subtitleStyle, fontSize: '11px' }}>
                  {customTags.length}/{maxCustomTags} custom tags
                </p>
              </div>
            )}
          </div>

          {/* Comment */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Comentario (opcional)</label>
            <textarea
              placeholder="Escribe tu experiencia en la cancha..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={200}
              style={textareaStyle}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <span style={counterStyle}>
            {selectedTags.length}/{maxPredefinedTags} tags seleccionados
          </span>
          <button style={submitBtnStyle} onClick={handleSubmit} disabled={rating === 0 || isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Enviar Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateVenueReviewModal;
